import { Button, buttonVariants, Chip, ErrorBanner } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import { describeError } from '../../lib/api/client'
import type { FollowupPreviewItemOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { useMe } from '../auth/session'
import { LearnMore } from '../help/learn-more'
import { DRAFT_COUNT_DAYS, recentDraftCount, remindersQuery, useLabelFollowupPreview, usePreviewFollowups } from './api'
import { bodyText, RemindersPanel, type ReminderTab } from './reminders-panel'

function due(iso: string): string {
  const value = new Date(iso)
  return Number.isNaN(value.getTime()) ? 'Due time unavailable' : `Due ${formatWhen(value.toISOString(), { style: 'absolute' })}`
}

function PreviewCard({
  item,
  label,
  labeling,
  onLabel,
}: {
  item: FollowupPreviewItemOut
  label?: 'approve' | 'reject'
  labeling: boolean
  onLabel: (label: 'approve' | 'reject') => void
}) {
  const effective = label ?? item.label
  const kind =
    item.kind === 'followup'
      ? `Follow-up ${item.step} of ${item.total}`
      : item.kind === 'stale_notice'
        ? 'Closing notice'
        : 'Courtesy close'
  return (
    <li className="overflow-hidden rounded-lg border border-border bg-card" data-testid="followup-preview-card">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/30 px-3 py-2">
        <Chip tone="primary">{kind}</Chip>
        <Chip tone="outline">{item.source === 'riley' ? 'Riley' : 'Template'}</Chip>
        <Chip tone={item.check.allowed ? 'success' : 'warning'}>{item.check.allowed ? 'Disclosure passed' : 'Would withhold'}</Chip>
        <Link to="/case/$id" params={{ id: item.case_number }} className="font-medium text-primary">
          {item.case_number}
        </Link>
        <span className="ml-auto text-xs text-subtle">{due(item.due_at)}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 px-3 pt-3 text-xs">
        <dt className="text-subtle">To</dt>
        <dd>{item.to.join(', ') || 'No customer address'}</dd>
        <dt className="text-subtle">Cc</dt>
        <dd>{item.cc.join(', ') || '—'}</dd>
        <dt className="text-subtle">Subject</dt>
        <dd className="font-medium">{item.subject}</dd>
      </dl>
      <pre className="m-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/60 p-3 font-sans text-sm leading-6">
        {bodyText(item.body_html)}
      </pre>
      {item.close_note_text ? (
        <div className="mx-3 mb-3 rounded-md border border-border p-2 text-sm">
          <p className="mb-1 text-xs font-medium text-subtle">Close note</p>
          <p className="whitespace-pre-wrap">{item.close_note_text}</p>
        </div>
      ) : null}
      {!item.check.allowed ? (
        <p className="mx-3 mb-2 text-xs text-warning">
          Check: {item.check.code ?? ((item.check.violations ?? []).join(', ') || 'withheld')}
        </p>
      ) : null}
      <div className="flex items-center justify-end gap-1.5 border-t border-border px-3 py-2">
        {effective ? (
          <span className="mr-auto text-xs text-muted-foreground">Recorded: {effective === 'approve' ? 'approved' : 'rejected'}</span>
        ) : null}
        <Button
          size="sm"
          variant={effective === 'approve' ? 'primary' : 'outline'}
          aria-label={`Approve ${item.case_number} preview`}
          disabled={labeling || Boolean(effective)}
          onClick={() => onLabel('approve')}
        >
          👍 Approve
        </Button>
        <Button
          size="sm"
          variant={effective === 'reject' ? 'destructive' : 'outline'}
          aria-label={`Reject ${item.case_number} preview`}
          disabled={labeling || Boolean(effective)}
          onClick={() => onLabel('reject')}
        >
          👎 Reject
        </Button>
      </div>
    </li>
  )
}

/**
 * `/followups` (#233, #276, #342): the one place for every automatic sender's output. Riley answer comparisons,
 * stale customer waits, shadow drafts, the sent and skipped log, the next run and the Off/Shadow/Auto switches, which used to open only from a button at the foot of Home's
 * "Close-out blocked" section. Managers and cover dispatchers only, like the reminders API it reads.
 */
export function FollowupsScreen({ tab, onTab }: { tab: ReminderTab; onTab: (tab: ReminderTab) => void }) {
  const me = useMe()
  const dispatcher = me?.role === 'manager' || me?.role === 'cover'
  const rows = useQuery({ ...remindersQuery(), enabled: dispatcher })
  const preview = usePreviewFollowups()
  const labeling = useLabelFollowupPreview()
  const [labels, setLabels] = React.useState<Record<number, 'approve' | 'reject'>>({})
  const drafts = rows.data ? recentDraftCount(rows.data) : null
  const subtitle =
    drafts === null
      ? 'Riley answer comparisons, stale customer waits, follow-ups, close-out reminders and vendor chase.'
      : `${drafts} shadow draft${drafts === 1 ? '' : 's'} in the last ${DRAFT_COUNT_DAYS} days. Riley answers, stale customer waits, follow-ups, reminders and vendor chase.`
  return (
    <Frame
      active="followups"
      railless
      main={
        <div className="flex h-full min-w-0 max-w-full flex-col overflow-x-clip overflow-y-auto">
          <PaneHeader
            title="Follow-ups"
            subtitle={subtitle}
            actions={
              <div className="flex items-center gap-1.5">
                <LearnMore route="/followups" />
                {me?.role === 'manager' ? (
                  <>
                    <Button size="sm" pending={preview.isPending} onClick={() => preview.mutate()}>
                      Preview next drafts
                    </Button>
                    <Link to="/admin/settings" hash="automation" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                      Automation settings
                    </Link>
                  </>
                ) : null}
              </div>
            }
          />
          <div className="max-w-4xl p-5">
            {preview.isError ? (
              <div className="mb-4">
                <ErrorBanner title="Couldn't preview the drafts" detail={describeError(preview.error)} onRetry={() => preview.mutate()} />
              </div>
            ) : null}
            {preview.data ? (
              <section className="mb-5 space-y-2" aria-label="Follow-up draft previews">
                <div>
                  <h2 className="font-semibold">Next drafts</h2>
                  <p className="text-sm text-muted-foreground">
                    Exact unattended-send wording and disclosure results. Reviewing does not send or consume a step.
                  </p>
                </div>
                {preview.data.items.length ? (
                  <ul className="space-y-3">
                    {preview.data.items.map((item) => (
                      <PreviewCard
                        key={item.event_id}
                        item={item}
                        label={labels[item.event_id]}
                        labeling={labeling.isPending && labeling.variables?.eventId === item.event_id}
                        onLabel={(label) =>
                          labeling.mutate(
                            { eventId: item.event_id, label },
                            { onSuccess: () => setLabels((current) => ({ ...current, [item.event_id]: label })) },
                          )
                        }
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
                    No follow-up or stale-close draft is available to preview.
                  </p>
                )}
                {labeling.isError ? (
                  <p className="text-sm text-destructive">Couldn't record that learning signal: {describeError(labeling.error)}</p>
                ) : null}
              </section>
            ) : null}
            {me && !dispatcher ? (
              <p className="text-sm text-muted-foreground" data-testid="followups-not-allowed">
                Follow-ups are reviewed by managers and cover dispatchers. Each case shows its own follow-up trail.
              </p>
            ) : (
              <RemindersPanel manager={me?.role === 'manager'} tab={tab} onTab={onTab} />
            )}
          </div>
        </div>
      }
    />
  )
}
